'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import type { AuthGuardEvent } from '@/lib/frontend/auth'

function Modal({
  title,
  description,
  actionHref,
  actionLabel,
  onClose
}: {
  title: string
  description: string
  actionHref: string
  actionLabel: string
  onClose: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'rgba(0,0,0,0.55)',
        display: 'grid',
        placeItems: 'center',
        padding: 16
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 'min(560px, 92vw)',
          borderRadius: 16,
          border: '1px solid var(--border)',
          background: 'var(--panel)',
          color: 'var(--text)',
          padding: 16,
          boxShadow: '0 16px 50px rgba(0,0,0,0.35)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontWeight: 1000, fontSize: 18 }}>{title}</div>
        <div style={{ marginTop: 10, color: 'var(--muted)', lineHeight: 1.6 }}>{description}</div>
        <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link
            href={actionHref}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid #0b0b0f',
              background: '#0b0b0f',
              color: '#ffffff',
              textDecoration: 'none',
              fontWeight: 900
            }}
          >
            {actionLabel}
          </Link>
          <button
            onClick={onClose}
            style={{
              padding: '10px 14px',
              borderRadius: 12,
              border: '1px solid var(--border)',
              background: 'var(--panel)',
              color: 'var(--text)',
              fontWeight: 900,
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AuthEvents() {
  const [modal, setModal] = useState<'credits' | 'upgrade' | null>(null)

  useEffect(() => {
    function onEvent(e: Event) {
      const evt = e as CustomEvent<AuthGuardEvent>
      const detail = evt.detail
      if (!detail) return
      if (detail.type === 'redirect') {
        window.location.href = detail.to
        return
      }
      if (detail.type === 'buy_credits') {
        setModal('credits')
        return
      }
      if (detail.type === 'upgrade_pro') {
        setModal('upgrade')
        return
      }
    }
    window.addEventListener('duo:auth-event', onEvent as any)
    return () => window.removeEventListener('duo:auth-event', onEvent as any)
  }, [])

  if (!modal) return null

  if (modal === 'credits') {
    return (
      <Modal
        title="Credits required"
        description="This action requires credits. Buy credits to continue."
        actionHref="/dashboard/billing"
        actionLabel="Buy credits"
        onClose={() => setModal(null)}
      />
    )
  }

  return (
    <Modal
      title="Upgrade required"
      description="This action requires the Pro plan. Upgrade to continue."
      actionHref="/dashboard/billing"
      actionLabel="Upgrade to Pro"
      onClose={() => setModal(null)}
    />
  )
}

